// externals
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

// libraries
import { SlideOut } from '@interstate/components/SlideOut';
import { Box } from '@interstate/components/Box';
import { Button } from '@interstate/components/Button';
import { TextArea } from '@interstate/components/TextArea';
import { Typography } from '@interstate/components/Typography';
import { Alert } from '@interstate/components/Alert';
import { ExclamationCircleIcon } from '@interstate/components/Icons';
import { apiConfigHelper, locHrefUtil, MANAGER_VIEW_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import * as bridgeUserSelectors from '@makemydeal/dr-dash-store/selectors/bridgeUserSelectors';
import { getDealXgId, getCommonOrgId } from '@makemydeal/dr-dash-store/selectors/offerRedux';

const NOT_SUBMITTED = 'NotSubmitted';
const SUCCESS = 'Success';
const FAILURE = 'Failure';

type SubmissionState = typeof NOT_SUBMITTED | typeof SUCCESS | typeof FAILURE;

export interface ReportIssueSlideOutProps {
    show: boolean;
    onHide: () => void;
}

export const ReportIssueSlideOut: React.FC<ReportIssueSlideOutProps> = ({ show, onHide }) => {
    const [description, setDescription] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submissionState, setSubmissionState] = useState<SubmissionState>(NOT_SUBMITTED);
    const [isTouched, setIsTouched] = useState(false);
    const dealerId = useSelector(dealerSelectors.getDealerId);
    const dealerName = useSelector(dealerSelectors.getDealerName);
    const commonOrgId = useSelector(getCommonOrgId);
    const userName = useSelector(bridgeUserSelectors.getFullName);
    const dealXgId = useSelector(getDealXgId);

    const handleCancel = () => {
        setDescription('');
        setSubmissionState(NOT_SUBMITTED);
        setIsTouched(false);
        onHide();
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setSubmissionState(NOT_SUBMITTED);

        try {
            const { url } = apiConfigHelper.buildBffApiUrl(MANAGER_VIEW_APP_PREFIX, locHrefUtil.getLocHref(), '/reportIssue');

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    message: description,
                    dealerId,
                    dealerName,
                    commonOrgId,
                    userName,
                    dealXgId
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            setDescription('');
            setSubmissionState(SUCCESS);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to report issue:', error);
            setSubmissionState(FAILURE);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDescriptionChange = (e: any) => {
        const value = e.target.value;
        setDescription(value);
        // Mark as touched once user starts typing
        if (!isTouched && value.length > 0) {
            setIsTouched(true);
        }
    };

    const handleDescriptionBlur = () => {
        // Mark as touched on blur if there was any interaction
        if (description.length > 0 || isTouched) {
            setIsTouched(true);
        }
    };

    const showValidationError = isTouched && description.trim().length === 0;

    return (
        <SlideOut
            data-testid="report-issue-slideout"
            show={show}
            position="right"
            onHide={handleCancel}
            header="Report an Issue"
            panelWidth="480px"
            sx={{
                '.ids-SlideOut-content': {
                    overflowY: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    padding: 0
                }
            }}
            footer={{
                footerComponent: (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 2,
                            backgroundColor: '#f5f5f5',
                            padding: 2
                        }}
                    >
                        {submissionState === SUCCESS ? (
                            <Button buttonStyle="primary" onClick={handleCancel} data-testid="close-button">
                                Close
                            </Button>
                        ) : (
                            <>
                                <Button buttonStyle="tertiary" onClick={handleCancel} data-testid="cancel-button">
                                    Cancel
                                </Button>
                                <Button
                                    buttonStyle="primary"
                                    onClick={handleSubmit}
                                    disabled={!description.trim() || isSubmitting}
                                    data-testid="submit-button"
                                >
                                    Submit
                                </Button>
                            </>
                        )}
                    </Box>
                )
            }}
        >
            {submissionState === SUCCESS ? (
                <Box
                    sx={{
                        flexGrow: 1,
                        padding: '1.5rem',
                        display: 'flex',
                        alignItems: 'flex-start'
                    }}
                >
                    <Alert type="success" title="Issue reported successfully" data-testid="success-alert" disableSuccessFade>
                        Thank you, please report this issue to your performance manager so they can keep you updated on progress.
                        This submission will allow us to find the exact replay of your session to understand the situation.
                    </Alert>
                </Box>
            ) : (
                <Box
                    sx={{
                        flexGrow: 1,
                        padding: '0 1.6rem 1.6rem',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px'
                    }}
                >
                    <Typography
                        variant="body-sm"
                        sx={{
                            color: 'grey',
                            fontSize: '1rem',
                            fontWeight: 500
                        }}
                    >
                        This submission will allow us to find the exact replay of your session so no screenshots or recordings are
                        necessary. Please also report this issue to your performance manager so they can keep you updated on
                        progress.
                    </Typography>
                    <TextArea
                        name="issue-description"
                        label="Description"
                        placeholder="Details of the issue"
                        value={description}
                        onChange={handleDescriptionChange}
                        onBlur={handleDescriptionBlur}
                        rows={8}
                        data-testid="issue-description-textarea"
                    />
                    {showValidationError && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                marginTop: '-12px'
                            }}
                            data-testid="description-error"
                        >
                            <Box
                                sx={{
                                    color: '#d32f2f',
                                    fontSize: '1rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    lineHeight: 1.66
                                }}
                            >
                                <ExclamationCircleIcon />
                            </Box>
                            <Typography
                                variant="body-sm"
                                sx={{
                                    color: '#d32f2f',
                                    fontSize: '0.75rem',
                                    lineHeight: 1.66
                                }}
                            >
                                Please enter a valid response.
                            </Typography>
                        </Box>
                    )}
                    {submissionState === FAILURE && (
                        <Alert type="error" title="Failed to report issue" data-testid="error-alert">
                            An error occurred while submitting your report. Please try again.
                        </Alert>
                    )}
                </Box>
            )}
        </SlideOut>
    );
};
